import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

/**
 * useDeleteModal
 * Manages single-item and bulk-delete modal state, plus row-selection logic.
 *
 * @param {Array}    currentItems  – the currently visible rows
 */
const useDeleteModal = (currentItems = []) => {
    const [deleteModal,    setDeleteModal]    = useState({ isOpen: false, id: null, isBulk: false });
    const [selectedItems,  setSelectedItems]  = useState([]);
    const [selectAll,      setSelectAll]      = useState(false);

    // Keep "select all" in sync when data or selection changes
    useEffect(() => {
        if (currentItems.length > 0 && selectedItems.length > 0) {
            setSelectAll(currentItems.every((item) => selectedItems.includes(item._id)));
        } else {
            setSelectAll(false);
        }
    }, [currentItems, selectedItems]);

    // ── Modal open / close ────────────────────────────────────────────────────
    const openDeleteModal = (id = null, isBulk = false) => {
        if (isBulk && selectedItems.length === 0) {
            toast.info('No items selected for deletion.');
            return;
        }
        setDeleteModal({ isOpen: true, id, isBulk });
    };

    const closeDeleteModal = () =>
        setDeleteModal({ isOpen: false, id: null, isBulk: false });

    // ── Row selection ─────────────────────────────────────────────────────────
    const handleSelectAll = () => {
        if (!selectAll) {
            setSelectedItems(currentItems.map((item) => item._id));
        } else {
            setSelectedItems([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter((i) => i !== id));
            if (selectAll) setSelectAll(false);
        } else {
            const next        = [...selectedItems, id];
            const allSelected = currentItems.every((item) => next.includes(item._id));
            setSelectedItems(next);
            setSelectAll(allSelected);
        }
    };

    const clearSelection = () => {
        setSelectedItems([]);
        setSelectAll(false);
    };

    return {
        deleteModal,
        openDeleteModal,
        closeDeleteModal,
        selectedItems,
        setSelectedItems,
        selectAll,
        handleSelectAll,
        handleSelectItem,
        clearSelection,
    };
};

export default useDeleteModal;